// src/pages/JoinCase.js
import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../utils/api";
import {
  FaArrowLeft,
  FaUserTie,
  FaFilePdf,
  FaCalendarAlt,
  FaMoneyBill,
  FaGavel,
  FaLock,
  FaEye,
  FaDownload,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

function Join() {
  const [caseCode, setCaseCode] = useState("");
  const [caseData, setCaseData] = useState(null);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [previewFile, setPreviewFile] = useState(null);
  const [openSection, setOpenSection] = useState(null);

  const navigate = useNavigate();

  /* ===========================================================
    Helpers
  =========================================================== */
  const toggleSection = (section) =>
    setOpenSection(openSection === section ? null : section);

  const formatDateAr = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("ar-EG");
    } catch {
      return "—";
    }
  };

  const formatDateTimeAr = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleString("ar-EG");
    } catch {
      return "—";
    }
  };

  const canPreview = (url = "") =>
    [".pdf", ".png", ".jpg", ".jpeg", ".webp"].some((ext) =>
      String(url).toLowerCase().endsWith(ext)
    );

  /* ===========================================================
    Sorted meetings (Meetings Collection)
  =========================================================== */
  const sortedMeetings = useMemo(() => {
    const list = Array.isArray(caseData?.meetings) ? caseData.meetings : [];

    const toTime = (m) => {
      const d = new Date(m.date);
      if (m.time) {
        const [hh, mm] = m.time.split(":").map(Number);
        d.setHours(hh || 0, mm || 0, 0, 0);
      }
      return d.getTime();
    };

    return [...list].sort((a, b) => toTime(a) - toTime(b));
  }, [caseData]);

  /* ===========================================================
    Join Case
  =========================================================== */
  const handleJoin = async () => {
    const code = String(caseCode || "").trim();
    if (!code) return setError("⚠️ من فضلك أدخل كود القضية");

    try {
      setLoading(true);
      setError("");

      const res = await api.post("/cases/join", { caseCode: code });

      setCaseData(res.data.case);
      setOpenSection("client"); // ✅ افتح بيانات العميل تلقائيًا
    } catch (err) {
      setCaseData(null);
      setOpenSection(null);

      const msg =
        err?.response?.data?.message ||
        "❌ لا توجد قضية بهذا الكود أو حدث خطأ في السيرفر";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  /* ===========================================================
    Sections
  =========================================================== */
  const sections = useMemo(() => {
    if (!caseData) return [];

    return [
      {
        title: "👤 بيانات العميل",
        icon: <FaUserTie />,
        key: "client",
        content: (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <p>👤 الاسم: {caseData.client?.name || "—"}</p>
            <p>📞 الهاتف: {caseData.client?.phone || "—"}</p>
            <p>📧 البريد الإلكتروني: {caseData.client?.email || "—"}</p>
            <p>🏠 العنوان: {caseData.client?.address || "—"}</p>
          </div>
        ),
      },

      {
        title: "📎 ملفات القضية",
        icon: <FaFilePdf />,
        key: "files",
        content: caseData.files?.length ? (
          <ul className="space-y-3">
            {caseData.files.map((file, i) => (
              <li
                key={i}
                className="bg-gray-900 p-3 rounded-lg border border-gray-700 hover:border-yellow-500 transition"
              >
                <div className="flex justify-between items-center gap-2">
                  <p className="text-yellow-400 font-semibold truncate max-w-[70%]">
                    📄 {file.fileName}
                  </p>

                  {canPreview(file.filePath) ? (
                    <button
                      onClick={() => setPreviewFile(file.filePath)}
                      className="text-yellow-400 hover:text-yellow-300 flex items-center gap-1 font-bold"
                    >
                      <FaEye /> عرض
                    </button>
                  ) : (
                    <a
                      href={file.filePath}
                      target="_blank"
                      rel="noreferrer"
                      className="text-green-400 hover:text-green-300 flex items-center gap-1 font-bold"
                    >
                      <FaDownload /> تحميل
                    </a>
                  )}
                </div>

                {file.ocrText ? (
                  <details className="mt-2 bg-gray-800 p-2 rounded text-sm text-gray-300">
                    <summary className="cursor-pointer text-green-400 font-bold">
                      🧠 عرض النص المستخرج OCR
                    </summary>
                    <p className="mt-2 whitespace-pre-wrap leading-relaxed">
                      {file.ocrText}
                    </p>
                  </details>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-400">📭 لا توجد ملفات بعد</p>
        ),
      },

      /* ✅ أهم تعديل: مواعيد القضية */
      {
        title: "📅 مواعيد القضية",
        icon: <FaCalendarAlt />,
        key: "meetings",
        content: (
          <div className="space-y-4">
            {/* ✅ تواريخ القضية الأساسية اللي انت تقصدها */}
            <div className="bg-gray-900 p-3 rounded-lg border border-gray-700 space-y-2">
              <p className="text-yellow-400 font-extrabold">
                📌 التواريخ الأساسية (تواريخ القضية)
              </p>

              <p>
                🟢 تاريخ بداية القضية:{" "}
                <span className="text-gray-200 font-bold">
                  {formatDateAr(caseData.startDate)}
                </span>
              </p>

              <p>
                🔴 تاريخ الجلسة / المحاكمة:{" "}
                <span className="text-gray-200 font-bold">
                  {formatDateAr(caseData.trialDate)}
                </span>
              </p>
            </div>

            {/* ✅ Meetings Collection */}
            <div className="bg-gray-900 p-3 rounded-lg border border-gray-700">
              <p className="text-green-400 font-extrabold mb-2">
                📌 المواعيد المضافة داخل القضية (Meetings)
              </p>

              {sortedMeetings?.length ? (
                <ul className="space-y-2">
                  {sortedMeetings.map((m, i) => (
                    <li
                      key={i}
                      className="bg-gray-800 p-3 rounded border border-gray-700"
                    >
                      <p className="text-yellow-300 font-bold break-words">
                        📍 {m.title}
                      </p>

                      <p className="text-gray-200 mt-1">
                        ⏰ {formatDateAr(m.date)} {m.time || ""}
                      </p>

                      <p className="text-gray-400 mt-2 break-words">
                        📝 {m.desc || "—"}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-400">لا توجد Meetings مرتبطة بعد</p>
              )}
            </div>
          </div>
        ),
      },

      {
        title: "💵 سجل الدفعات",
        icon: <FaMoneyBill />,
        key: "payments",
        content: caseData.payments?.length ? (
          <ul className="space-y-2">
            {caseData.payments.map((p, i) => (
              <li
                key={i}
                className="bg-gray-900 p-3 rounded border border-gray-700"
              >
                <p className="text-yellow-300 font-bold">
                  💰 المبلغ: {p.amount} ج.م
                </p>
                <p className="text-gray-200">📝 {p.note || "—"}</p>
                <p className="text-gray-400 text-sm mt-1">
                  {formatDateTimeAr(p.date)}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-400">لا توجد دفعات بعد</p>
        ),
      },
    ];
  }, [caseData, sortedMeetings]);

  /* ===========================================================
    Render
  =========================================================== */
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-950 text-white p-4 bg-cover bg-center relative"
      style={{
        backgroundImage:
          "url('https://images.unsplash.com/photo-1589998059171-988d887df646?auto=format&fit=crop&w=1500&q=80')",
      }}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>

      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 bg-gray-900/95 border border-yellow-500/60 p-6 sm:p-8 rounded-2xl shadow-2xl w-full max-w-3xl"
        dir="rtl"
      >
        {!caseData ? (
          <div className="text-center">
            <FaGavel className="text-5xl text-yellow-400 mb-4 mx-auto animate-bounce" />
            <h2 className="text-2xl sm:text-3xl font-extrabold text-yellow-400 mb-2">
              👨‍💼 دخول كمحامي مساعد
            </h2>

            <p className="text-gray-400 mb-6">
              أدخل كود القضية للوصول إلى تفاصيلها وملفاتها ومواعيدها ✅
            </p>

            <div className="relative mb-4">
              <FaLock className="absolute top-4 right-4 text-yellow-400" />

              <input
                value={caseCode}
                onChange={(e) => setCaseCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleJoin();
                }}
                placeholder="أدخل كود القضية..."
                className="w-full p-4 pr-12 bg-gray-800 border border-gray-700 rounded-xl text-white focus:border-yellow-400 outline-none"
              />
            </div>

            {error ? (
              <p className="text-red-300 bg-red-900/30 py-3 rounded-xl mb-3 border border-red-600">
                {error}
              </p>
            ) : null}

            <button
              onClick={handleJoin}
              disabled={loading}
              className={`w-full py-3 rounded-xl font-extrabold transition ${
                loading
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-yellow-500 hover:bg-yellow-600 text-black"
              }`}
            >
              {loading ? "⏳ جاري البحث..." : "دخول"}
            </button>

            <button
              onClick={() => navigate("/")}
              className="w-full mt-3 bg-gray-700 hover:bg-gray-600 text-yellow-400 py-3 rounded-xl font-bold"
            >
              <FaArrowLeft className="inline ml-2" /> رجوع
            </button>
          </div>
        ) : (
          <div>
            <h3 className="text-xl sm:text-2xl text-yellow-400 text-center font-extrabold mb-4">
              ⚖️ تفاصيل القضية: {caseData.title}
            </h3>

            {/* Summary */}
            <div className="bg-gray-800/80 border border-yellow-500 p-4 rounded-xl mb-5 space-y-1">
              <p>⚖️ النوع: {caseData.type || "—"}</p>
              <p>
                📅 من {formatDateAr(caseData.startDate)} إلى{" "}
                {formatDateAr(caseData.trialDate)}
              </p>
              <p>💰 السعر: {caseData.price || "—"} ج.م</p>
              <p>📝 الملاحظات: {caseData.notes || "—"}</p>
            </div>

            {/* Sections */}
            {sections.map((section) => (
              <div key={section.key} className="mb-3">
                <button
                  onClick={() => toggleSection(section.key)}
                  className="w-full bg-gray-800 hover:bg-gray-700 text-yellow-400 px-4 py-3 rounded-xl flex justify-between items-center text-right font-extrabold border border-gray-700"
                >
                  <span className="flex items-center gap-2">
                    {section.icon} {section.title}
                  </span>
                  {openSection === section.key ? (
                    <FaChevronUp />
                  ) : (
                    <FaChevronDown />
                  )}
                </button>

                <AnimatePresence mode="wait">
                  {openSection === section.key && (
                    <motion.div
                      key={section.key}
                      initial={{ opacity: 0, y: -15, height: 0 }}
                      animate={{ opacity: 1, y: 0, height: "auto" }}
                      exit={{ opacity: 0, y: -15, height: 0 }}
                      transition={{
                        duration: 0.3,
                        ease: [0.25, 0.8, 0.5, 1],
                      }}
                      className="bg-gray-800/80 border border-yellow-500/70 mt-2 rounded-xl p-4 text-gray-200 text-sm shadow-md shadow-yellow-500/10"
                    >
                      {section.content}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}

            {/* Actions */}
            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={() => {
                  setCaseData(null);
                  setCaseCode("");
                  setOpenSection(null);
                  setError("");
                }}
                className="bg-yellow-500 hover:bg-yellow-600 text-black py-3 rounded-xl font-extrabold"
              >
                🔄 إدخال كود جديد
              </button>

              <button
                onClick={() => navigate("/")}
                className="bg-gray-700 hover:bg-gray-600 text-yellow-400 py-3 rounded-xl font-bold"
              >
                <FaArrowLeft className="inline ml-2" /> رجوع
              </button>
            </div>

            {/* Preview modal */}
            {previewFile && (
              <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex justify-center items-center z-50">
                <div className="relative w-11/12 md:w-3/4 lg:w-1/2 bg-gray-900 rounded-xl shadow-lg overflow-hidden border border-gray-700">
                  <button
                    onClick={() => setPreviewFile(null)}
                    className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full z-10"
                  >
                    <FaTimes />
                  </button>

                  <iframe
                    src={previewFile}
                    title="file preview"
                    className="w-full h-[80vh]"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default Join;
